// src/utils/pgResultProbe.js
//
// NZSC-4015: PG 결제창 결과를 한 곳에서 기록한다.
//
// 플러그인($inicisPayment/$payplePayment) 밖에서 SDK 전역을 직접 부르는 곳이 있다
// (IMP.request_pay 7파일, PaypleCpayAuthCheck 3파일). 호출부를 손대는 대신 **SDK 전역 자체를
// 감싼다** — 어디서 부르든 콜백 결과가 먼저 여기를 지난다. SDK 스크립트가 main.js 보다 늦게
// 실행되면 window 접근자로 대입 시점을 잡아 그때 감싼다.
//
// 결과가 실패/취소일 때만 기록한다(성공은 오류 문맥이 아니다). 콜백 자체는 그대로 호출한다 —
// 이 모듈이 던지면 결제 흐름이 끊기므로 모든 경로를 try/catch 로 막는다.
//
// NZSC-4126: 같은 래퍼에서 결제창 시도(open)·결과(result)를 api-public 에 기록한다(payAttemptLog).
// 원 인자·콜백은 그대로 넘기고, 기록은 fire-and-forget(await 없음)이다.

import { recordPgResult } from "./errorContext";
import {
  consumeRedirectLanding,
  paypleResult,
  portoneResult,
  recordPayAttemptOpen,
  recordPayAttemptResult,
} from "./payAttemptLog";

const WRAPPED = Symbol.for("nz.pgResultProbe.wrapped");

function wrapPortone(imp) {
  try {
    if (!imp || typeof imp.request_pay !== "function" || imp.request_pay[WRAPPED]) return imp;
    const original = imp.request_pay;
    const wrapped = function (params, callback, ...rest) {
      let attemptKey = null;
      try {
        attemptKey = recordPayAttemptOpen({
          pg: "portone",
          payMethod: params && params.pay_method,
          amount: params && params.amount,
          orderNo: params && params.merchant_uid,
          hintUrl: params && params.m_redirect_url,
          redirect: typeof callback !== "function",
        });
      } catch (e) {
        /* 기록 실패가 결제창을 막아선 안 된다 */
      }
      // 콜백 없는 호출(모바일 리다이렉트 등)은 호출 형태를 바꾸지 않는다 — 결과는 착지 페이지에서 기록
      if (typeof callback !== "function") return original.apply(this, arguments);
      const probed = function (res, ...cbRest) {
        try {
          recordPayAttemptResult({ attemptKey, pg: "portone", ...portoneResult(res) });
        } catch (e) {
          /* noop */
        }
        try {
          if (!res || !res.success) {
            recordPgResult({
              provider: "portone",
              code: res?.error_code || (res?.imp_success === false ? "imp_success=false" : "no-result"),
              message: res?.error_msg,
            });
          }
        } catch (e) {
          /* 기록 실패가 결제 콜백을 막아선 안 된다 */
        }
        // SDK 가 준 this·추가 인자를 그대로 넘긴다
        return callback.call(this, res, ...cbRest);
      };
      return original.call(this, params, probed, ...rest);
    };
    wrapped[WRAPPED] = true;
    imp.request_pay = wrapped;
  } catch (e) {
    /* noop */
  }
  return imp;
}

function wrapPayple(fn) {
  try {
    if (typeof fn !== "function" || fn[WRAPPED]) return fn;
    const wrapped = function (obj) {
      let attemptKey = null;
      // 0원 빌링키 등록(PCD_PAY_WORK=AUTH)은 결제 시도가 아니다 — open·result 모두 기록하지 않는다
      let isAuthOnly = false;
      try {
        isAuthOnly = !!obj && obj.PCD_PAY_WORK === "AUTH";
      } catch (e) {
        /* noop */
      }
      try {
        if (!isAuthOnly) attemptKey = recordPayAttemptOpen({
          pg: "payple",
          payMethod: obj && obj.PCD_PAY_TYPE,
          amount: obj && obj.PCD_PAY_TOTAL,
          orderNo: obj && obj.PCD_PAY_OID,
          hintUrl: obj && obj.PCD_RST_URL,
          // 콜백이 없으면 모바일 리다이렉트 — 결과는 착지 페이지(consumeRedirectLanding)에서
          redirect: !(obj && typeof obj.callbackFunction === "function"),
        });
      } catch (e) {
        /* 기록 실패가 결제창을 막아선 안 된다 */
      }
      // 호출자의 요청 객체는 바꾸지 않는다 — 같은 객체로 다시 열면 래퍼가 겹쳐 이전 시도에 결과가 붙는다.
      // 콜백만 바꾼 얕은 복사본을 SDK 에 넘긴다.
      let payload = obj;
      try {
        const cb = obj && obj.callbackFunction;
        if (typeof cb === "function") {
          const probedCallback = function (res, ...cbRest) {
            try {
              if (!isAuthOnly) recordPayAttemptResult({ attemptKey, pg: "payple", ...paypleResult(res) });
            } catch (e) {
              /* noop */
            }
            try {
              if (!res || res.PCD_PAY_RST !== "success") {
                recordPgResult({
                  provider: "payple",
                  code: res?.PCD_PAY_CODE || res?.PCD_PAY_RST || "no-result",
                  message: res?.PCD_PAY_MSG,
                });
              }
            } catch (e) {
              /* noop */
            }
            return cb.call(this, res, ...cbRest);
          };
          payload = { ...obj, callbackFunction: probedCallback };
        }
      } catch (e) {
        payload = obj;
      }
      return fn.call(this, payload, ...Array.prototype.slice.call(arguments, 1));
    };
    wrapped[WRAPPED] = true;
    return wrapped;
  } catch (e) {
    return fn;
  }
}

/**
 * window 의 SDK 전역을 감싼다 — 지금 있으면 즉시 감싸고, **이후 다시 대입돼도**(SDK 지연/재로드) 접근자가
 * 다시 감싼다. 실브라우저 검증에서 "이미 있던 값만 제자리 래핑"은 재대입 한 번에 풀렸다.
 * 접근자를 못 거는 전역(classic script 의 function 선언 = non-configurable)은 제자리 래핑으로 폴백한다.
 */
function hookGlobal(name, wrap) {
  if (typeof window === "undefined") return;
  try {
    let value = window[name] === undefined ? undefined : wrap(window[name]);
    try {
      Object.defineProperty(window, name, {
        configurable: true,
        enumerable: true,
        get() {
          return value;
        },
        set(v) {
          value = wrap(v);
        },
      });
    } catch (e) {
      // non-configurable — 제자리 래핑만 (writable 이면 대입은 된다)
      if (value !== undefined) window[name] = value;
    }
  } catch (e) {
    /* 기록 없이 결제는 정상 동작 */
  }
}

export function installPgResultProbe() {
  hookGlobal("IMP", wrapPortone);
  hookGlobal("PaypleCpayAuthCheck", wrapPayple);
  // 모바일 리다이렉트 착지(풀 페이지 로드) 결과 — 부팅 1회
  try {
    consumeRedirectLanding();
  } catch (e) {
    /* noop */
  }
}

export const __test = { wrapPortone, wrapPayple, hookGlobal, WRAPPED };
